import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import axios from 'axios';
import {
  Home, Library as LibraryIcon, FolderOpen, ListMusic, Disc3, BarChart3,
  History, Settings, Search, Bell, Upload, Send, PanelRightOpen, PanelRightClose, MoreVertical,
  ChevronDown, ChevronRight, Plus, Sparkles, Loader2, Trash2, Menu, Music, Monitor,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { usePlayer } from '@/context/PlayerContext';
import { API } from '@/App';
import { CreditCard } from 'lucide-react';
import CreditBalance from '@/components/CreditBalance';
import CommandPalette from '@/components/CommandPalette';
import BrandLogo from '@/components/BrandLogo';
import MixQualityCard from '@/components/MixQualityCard';
import SafeImg from '@/components/SafeImg';

const BRIEF_REGEX = /<SET_BRIEF>([\s\S]*?)<\/SET_BRIEF>/;
function extractBrief(text) {
  const m = (text || '').match(BRIEF_REGEX);
  if (!m) return null;
  try { return JSON.parse(m[1].trim()); }
  catch { return null; }
}
function cleanReply(text) {
  return (text || '').replace(BRIEF_REGEX, '').replace(/```json[\s\S]*?```/g, '').trim();
}

function formatRelativeTime(iso) {
  if (!iso) return 'just now';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return 'just now';
  const diff = (Date.now() - then) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/* ── Contextual AI explanations per route ─────────────────────── */
const PAGE_EXPLANATIONS = {
  '/dashboard':  "You're on the Dashboard — your DJ command center. The widgets here show your library size, recent AI sets, energy flow, and harmonic analysis at a glance. Everything updates live as you build and upload.",
  '/library':    "This is your Library — every track you've uploaded and analyzed lives here. Filter by BPM, Camelot key, or genre. Drag any track onto a crate in the sidebar to organise it instantly.",
  '/upload':     "Welcome to the Upload page. Drop your MP3 files here to bring them into CRATELAB. Uploading costs a small credit based on file size, and you can queue full AI analysis (BPM, key, energy curve) for 2 credits per track.",
  '/sets':       "AI Generated Sets — this is where your AI-built setlists live. Each set was created by the assistant using your BPM, key, and vibe preferences. Open any set to preview, edit, or export the tracklist.",
  '/crates':     "Crates are your personal folders. Group tracks by vibe, event, or client — whatever makes sense for how you DJ. Drop tracks from the Library straight onto a crate to add them instantly.",
  '/playlists':  "Playlists are manual curations you build yourself. Unlike AI sets, you pick every track. Use them for specific events, client briefs, or reference mixes.",
  '/analytics':  "Analytics shows a high-level view of your CRATELAB activity — uploads per month, analysis jobs, assistant chat sessions, and credit consumption. Great for keeping track of how your library grows.",
  '/billing':    "Billing is where you manage your credit balance. Credits are used for uploads, track analysis, AI chat, and set generation. View your full transaction history and top up your balance here.",
  '/profile':    "Settings lets you update your profile, customise AI Assistant preferences (playing style, favourite genres), manage which dashboard widgets are visible, and control notification behaviour.",
  '/mixes':      "Mixes is where your recorded or imported mix sessions live. Attach a setlist to a mix, mark timestamps, and let the AI score the harmonic flow of the full mix.",
};

const NAV_ITEMS = [
  { icon: Home,        label: 'Home',       path: '/dashboard'  },
  { icon: LibraryIcon, label: 'Library',    path: '/library'    },
  // Crates and Playlists are rendered as collapsible groups below
  { icon: Upload,      label: 'Upload',     path: '/upload'     },
  { icon: ListMusic,   label: 'Sets',       path: '/sets'       },
  { icon: Disc3,       label: 'Mixes',      path: '/mixes'      },
  { icon: Monitor,     label: 'Analyzer',   path: '/analyzer'   },
  { icon: BarChart3,   label: 'Analytics',  path: '/analytics'  },
  { icon: CreditCard,  label: 'Billing',    path: '/billing'    },
  { icon: Settings,    label: 'Settings',   path: '/profile'    },
];

export default function DashboardShell({ children, embeddedHeader }) {
  const { user } = useAuth() || {};
  const { playingTrack } = usePlayer() || {};
  const navigate = useNavigate();
  const location = useLocation();

  // Rail collapse state — persisted across page navigations within session.
  // Default: closed on mobile (<xl) so it doesn't cover the page on first load.
  const [railOpen, setRailOpen] = useState(() => {
    try {
      const stored = localStorage.getItem('cratelab_rail_open');
      if (typeof window !== 'undefined' && window.innerWidth < 1280) return false;
      return stored !== '0';
    } catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('cratelab_rail_open', railOpen ? '1' : '0'); } catch {}
  }, [railOpen]);

  // Library context (recent crates/playlists/genres) — feeds the AI awareness greeting.
  // Fetched once on mount; before this we refetched a 43KB payload on every route
  // change, adding 200-1400ms of latency to every nav. If the user creates a new
  // crate/playlist we don't need to refetch (the sidebar updates optimistically
  // and the next full-page load picks up the diff).
  const [libCtx, setLibCtx] = useState(null);
  useEffect(() => {
    axios.get(`${API}/dj/library-context`).then(r => setLibCtx(r.data)).catch(() => {});
  }, []);

  // Coach behavior profile — what the assistant has *learned* from past sets.
  // Surfaced as a "Coach Memory" pill near the assistant header so the user
  // can see the bot is growing with them. Refreshes after each build.
  const [behaviorProfile, setBehaviorProfile] = useState(null);
  const loadBehaviorProfile = useCallback(() => {
    axios.get(`${API}/dj/behavior-profile`)
      .then(r => setBehaviorProfile(r.data))
      .catch(() => {});
  }, []);
  useEffect(() => { loadBehaviorProfile(); }, [loadBehaviorProfile]);

  // AI Assistant state — shared across every authenticated page
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  // Notifications log — populated whenever the assistant proactively pushes
  // a message (e.g. first-login intro, Import-Tracks redirect explainer).
  // Persisted to localStorage so the Bell badge survives reloads.
  const [notifications, setNotifications] = useState(() => {
    try { return JSON.parse(localStorage.getItem('cratelab_notifs') || '[]'); }
    catch { return []; }
  });
  const [notifOpen, setNotifOpen] = useState(false);
  const unreadCount = notifications.filter((n) => !n.read).length;
  const [sessionId, setSessionId] = useState(() => {
    try {
      const stored = localStorage.getItem('cratelab_dj_session');
      if (stored) return stored;
    } catch { /* ignore */ }
    const fresh = `shell-${Date.now()}`;
    try { localStorage.setItem('cratelab_dj_session', fresh); } catch { /* ignore */ }
    return fresh;
  });
  const [coachPrefs, setCoachPrefs] = useState(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const chatEndRef = useRef(null);

  // Load persisted history and current DJ mode on mount + when session resets
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/dj/sessions/${sessionId}`);
        if (cancelled) return;
        const prior = (data?.messages || [])
          .filter((m) => (m.role === 'user' || m.role === 'assistant') && m.content)
          .map((m) => {
            // Assistant replies are stored verbatim by the backend, so they
            // can carry the <SET_BRIEF>{…}</SET_BRIEF> handshake tag the
            // coach emits for the UI to convert into a "Save as Set" chip.
            // Strip the tag from displayed text and re-attach the parsed
            // brief so the CTA renders on restored history exactly like a
            // fresh reply would.
            if (m.role !== 'assistant') {
              return { role: m.role, content: m.content };
            }
            const brief = extractBrief(m.content);
            const clean = cleanReply(m.content) || m.content;
            return { role: 'assistant', content: clean, ...(brief ? { brief } : {}) };
          });
        setMessages(prior.length
          ? prior
          : [{ role: 'assistant', content: 'How can I help you build your perfect set?' }]);
      } catch {
        if (!cancelled) {
          setMessages([{ role: 'assistant', content: 'How can I help you build your perfect set?' }]);
        }
      } finally {
        if (!cancelled) setHistoryLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/dj/coach-prefs`);
        if (!cancelled) setCoachPrefs(data || {});
      } catch {
        if (!cancelled) setCoachPrefs({});
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const startNewChat = () => {
    const fresh = `shell-${Date.now()}`;
    try { localStorage.setItem('cratelab_dj_session', fresh); } catch { /* ignore */ }
    setSessionId(fresh);
  };

  // Push an assistant-authored message into the chat + log it as a
  // notification entry. Called both internally (first-login intro, import
  // redirect explainer) and externally via the global `cratelab:assistant-push`
  // CustomEvent (any page can dispatch and the rail listens).
  const pushAssistantMessage = useCallback((content, opts = {}) => {
    if (!content || typeof content !== 'string') return;
    setMessages((m) => {
      // Avoid duplicating the very same message back-to-back if a component
      // dispatches twice on the same click.
      const last = m[m.length - 1];
      if (last && last.role === 'assistant' && last.content === content) return m;
      return [...m, { role: 'assistant', content }];
    });
    setNotifications((prev) => {
      const next = [
        {
          id: `n_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          title: opts.title || 'CRATELAB Assistant',
          body: content,
          ts: Date.now(),
          read: false,
        },
        ...prev,
      ].slice(0, 30); // cap to 30 most recent
      try { localStorage.setItem('cratelab_notifs', JSON.stringify(next)); } catch {}
      return next;
    });
    if (opts.openRail !== false) setRailOpen(true);
  }, []);

  // Global event bridge — any page can dispatch
  // `new CustomEvent('cratelab:assistant-push', { detail: { content, title?, openRail? } })`
  useEffect(() => {
    const handler = (e) => {
      const d = e.detail || {};
      pushAssistantMessage(d.content, d);
    };
    window.addEventListener('cratelab:assistant-push', handler);
    return () => window.removeEventListener('cratelab:assistant-push', handler);
  }, [pushAssistantMessage]);

  // First-login intro — when history has loaded AND the user has zero
  // tracks AND we haven't already shown the intro on this device, push a
  // welcome explaining the web upload flow. One-shot per browser.
  useEffect(() => {
    if (!historyLoaded || !libCtx) return;
    const totalTracks = libCtx.tracks_count ?? libCtx.totals?.tracks ?? libCtx.track_count ?? 0;
    if (totalTracks > 0) return;
    let seen = false;
    try { seen = localStorage.getItem('cratelab_intro_seen') === '1'; } catch {}
    if (seen) return;
    try { localStorage.setItem('cratelab_intro_seen', '1'); } catch {}
    pushAssistantMessage(
      "Welcome to CRATELAB \u{1F44B}\n\n" +
      "Your cloud library is empty right now. Here's how to get started:\n\n" +
      "  1. Head to the Upload page (click 'Upload' in the sidebar or the Import Tracks button above).\n" +
      "  2. Drop in your MP3 files — upload as many as you like.\n" +
      "  3. CRATELAB will automatically analyze BPM, Camelot key, energy and structure for every track.\n" +
      "  4. Once analyzed, your tracks appear in your Library and I can start building AI sets for you.\n\n" +
      "Once your tracks are in, I can suggest transitions, score playlist health, and build full sets based on your vibe. Ready to upload your first tracks?",
      { title: 'Getting started', openRail: true }
    );
  }, [historyLoaded, libCtx, pushAssistantMessage]);

  // Past chat sessions (lazy-loaded the first time the user opens the dropdown)
  const [pastOpen, setPastOpen] = useState(false);
  const [pastSessions, setPastSessions] = useState(null);
  const [pastLoading, setPastLoading] = useState(false);
  const pastBtnRef = useRef(null);

  // Mobile sidebar drawer state — collapsed by default below lg breakpoint
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  // Close the drawer whenever the route changes
  useEffect(() => { setMobileSidebarOpen(false); }, [location.pathname]);

  const loadPastSessions = async () => {
    setPastLoading(true);
    try {
      const { data } = await axios.get(`${API}/dj/sessions`);
      setPastSessions(data?.sessions || []);
    } catch {
      setPastSessions([]);
    } finally {
      setPastLoading(false);
    }
  };

  const togglePast = () => {
    if (!pastOpen) loadPastSessions();
    setPastOpen((o) => !o);
  };

  useEffect(() => {
    if (!pastOpen) return;
    const onDocClick = (e) => {
      if (pastBtnRef.current && !pastBtnRef.current.contains(e.target)) {
        setPastOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [pastOpen]);

  const switchToSession = (sid) => {
    if (!sid || sid === sessionId) { setPastOpen(false); return; }
    try { localStorage.setItem('cratelab_dj_session', sid); } catch { /* ignore */ }
    setSessionId(sid);
    setPastOpen(false);
  };

  const deletePastSession = async (sid) => {
    if (!sid) return;
    // Optimistic remove from the dropdown
    setPastSessions((arr) => (arr || []).filter((s) => s.session_id !== sid));
    try {
      await axios.delete(`${API}/dj/sessions/${sid}`);
      toast.success('Chat deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete chat');
      // Re-fetch to restore correct state on failure
      loadPastSessions();
      return;
    }
    // If they just deleted the active chat, spin up a fresh one
    if (sid === sessionId) startNewChat();
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const send = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setSending(true);
    try {
      // Build live context snapshot so the AI is always aware of the user's state
      const contextHint = libCtx ? [
        libCtx.tracks_count ? `library: ${libCtx.tracks_count} tracks` : '',
        libCtx.bpm_range ? `BPM ${libCtx.bpm_range.min}-${libCtx.bpm_range.max}` : '',
        libCtx.top_genres?.length ? `top genres: ${libCtx.top_genres.slice(0,3).join(', ')}` : '',
        `viewing: ${location.pathname}`,
      ].filter(Boolean).join(' · ') : `viewing: ${location.pathname}`;
      const { data } = await axios.post(`${API}/dj/assistant/chat`, {
        session_id: sessionId,
        message: text,
        context_hint: contextHint,
      });
      const brief = extractBrief(data.reply);
      const clean = cleanReply(data.reply);
      setMessages((m) => [...m, { role: 'assistant', content: clean || 'Got it.', brief }]);
      // Fire dry-run in background so we can show intersection chip before Save
      if (brief) {
        const newIdx = messages.length + 1; // +1 because we just pushed user + this assistant
        try {
          const dr = await axios.post(`${API}/dj/build-set/dry-run`, {
            bpm_min: brief.bpm_min,
            bpm_max: brief.bpm_max,
            target_genres: brief.target_genres || brief.genres || [],
            duration_min: brief.duration_min || brief.duration_minutes,
            vibe: brief.vibe || brief.mood,
          });
          setMessages((m) => m.map((msg, i) => (
            i === newIdx ? { ...msg, dryRun: dr.data } : msg
          )));
        } catch { /* non-fatal */ }
      }
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: 'Assistant is offline right now.', error: true }]);
    } finally {
      setSending(false);
    }
  };

  const [buildingIdx, setBuildingIdx] = useState(null);

  // Recent Uploads — fetched for sidebar quick-access list
  const [recentUploads, setRecentUploads] = useState([]);
  const [recentUploadsOpen, setRecentUploadsOpen] = useState(true);
  const prevPathnameRef = useRef(null);

  const fetchRecentUploads = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tracks`);
      const all = Array.isArray(data) ? data : (data?.tracks || []);
      // Show only tracks uploaded via the web UI, newest first
      const webTracks = all
        .filter((t) => t.upload_source === 'web' && !t.in_crate)
        .sort((a, b) => {
          const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
          const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
          return tb - ta;
        });
      setRecentUploads(webTracks);
    } catch { /* silent */ }
  }, []);

  // Fetch on mount AND whenever the user navigates (they may have just
  // uploaded something). One effect handles both — the initial pathname
  // triggers the effect on mount, so there's no need for a second useEffect.
  useEffect(() => { fetchRecentUploads(); }, [location.pathname, fetchRecentUploads]);

  // Route-change contextual AI explanation — pushes a brief description of
  // whichever section the user just navigated into, without opening the rail.
  useEffect(() => {
    if (!historyLoaded) return;
    const path = location.pathname;
    // Normalise detail pages back to their section root
    const sectionPath = path.startsWith('/track/') ? null
      : path.startsWith('/crate/') ? '/crates'
      : path.startsWith('/playlist/') ? '/playlists'
      : path;
    if (!sectionPath || sectionPath === prevPathnameRef.current) return;
    prevPathnameRef.current = sectionPath;
    const explanation = PAGE_EXPLANATIONS[sectionPath];
    if (!explanation) return;
    pushAssistantMessage(explanation, { title: 'About this section', openRail: false });
  }, [location.pathname, historyLoaded, pushAssistantMessage]);
  const buildSetFromBrief = async (brief, idx) => {
    if (!brief || buildingIdx != null) return;
    setBuildingIdx(idx);
    try {
      const payload = {
        name: brief.set_name || brief.name,
        bpm_min: brief.bpm_min,
        bpm_max: brief.bpm_max,
        start_track_id: brief.start_track_id || null,
        target_genres: brief.target_genres || brief.genres || [],
        must_play_ids: brief.must_play_ids || [],
        must_avoid_ids: brief.must_avoid_ids || [],
        duration_min: brief.duration_min || brief.duration_minutes,
        vibe: brief.vibe || brief.mood,
        energy_curve: brief.energy_curve,
        strict_harmonic: brief.strict_harmonic,
      };
      const { data } = await axios.post(`${API}/dj/build-set`, payload);
      const setName = data?.playlist?.name || 'Set';
      const trackCount = (data?.tracks || []).length;
      const warnings = data?.warnings || [];
      const relaxations = data?.relaxations || [];
      const libraryWarnings = data?.library_warnings || [];
      const warnSummary = warnings.length
        ? ` · ${warnings.length} transition warning${warnings.length > 1 ? 's' : ''}`
        : '';
      const description = [
        ...libraryWarnings.map(w => w.message),
        ...relaxations,
        ...(warnings.slice(0, 2).map(w => w.message)),
      ].slice(0, 3).join(' · ') || undefined;
      // Library-thin sets get a longer-lived warning toast so the DJ
      // notices the "import more" hint instead of getting near-identical
      // sets every time.
      const hasLibraryWarning = libraryWarnings.some(w => w.severity === 'high');
      if (hasLibraryWarning) {
        toast.warning(`Built "${setName}" · ${trackCount} tracks (library thin)`, {
          description,
          duration: 8000,
          action: { label: 'Open Sets', onClick: () => navigate('/sets') },
        });
      } else {
        toast.success(`Built "${setName}" · ${trackCount} tracks${warnSummary}`, {
          description,
          action: { label: 'Open Sets', onClick: () => navigate('/sets') },
        });
      }
      // Mark message brief as consumed
      setMessages((m) => m.map((msg, i) => i === idx
        ? { ...msg, brief: null, builtSetId: data?.playlist?.id, warnings, libraryWarnings, relaxations, trackCount }
        : msg));

      // The bot just learned from another set — refresh the memory pill.
      loadBehaviorProfile();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not build set');
    } finally {
      setBuildingIdx(null);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const firstName = (user?.name || user?.email || 'DJ').split(' ')[0].split('@')[0];

  return (
    <div className="fixed inset-0 flex bg-[#F5F7FB] overflow-hidden" data-testid="dashboard-shell">
      {/* Hide redundant per-page headers/titles when rendered inside the shell. */}
      <style>{`
        [data-testid="shell-main"] [data-page-header="redundant"] { display: none !important; }
      `}</style>
      {/* ── Left Sidebar — persistent on desktop, drawer on mobile ──── */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
          data-testid="mobile-sidebar-backdrop"
        />
      )}
      <aside
        className={`w-64 shrink-0 h-full flex flex-col border-r border-[#E2E8F0] bg-white
                    fixed lg:static inset-y-0 left-0 z-50 transform transition-transform duration-200
                    ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="sidebar"
      >
        {/* Logo — stacked: box icon on top, CRATELAB wordmark underneath */}
        <Link to="/dashboard" className="px-4 py-5 border-b border-[#E2E8F0] flex items-center justify-center" data-testid="sidebar-brand">
          <BrandLogo size="md" stack className="[&>img]:w-11 [&>img]:h-11 [&>span:last-child]:text-[17px]" />
        </Link>

        {/* Global search trigger (⌘K) — always visible on every page */}
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('cratelab:open-palette'))}
          data-testid="sidebar-search-btn"
          className="mx-3 mt-3 mb-1 flex items-center gap-2 px-3 py-2 rounded-lg bg-[#F8FAFC] border border-[#E2E8F0] hover:border-[#2563FF]/30 hover:bg-white transition-colors text-left"
        >
          <Search className="w-3.5 h-3.5 text-[#94A3B8] shrink-0" />
          <span className="flex-1 text-xs text-[#94A3B8] truncate">Search anything…</span>
          <kbd className="px-1.5 py-0.5 rounded border border-[#E2E8F0] text-[9px] font-mono text-[#94A3B8] bg-white">⌘K</kbd>
        </button>

        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {NAV_ITEMS.map((item) => {
            const active = item.path === '/dashboard'
              ? location.pathname === '/dashboard'
              : location.pathname.startsWith(item.path);
            const node = (
              <Link
                key={item.label}
                to={item.path}
                data-testid={`sidebar-nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-[15px] font-medium transition-all ${
                  active
                    ? 'bg-[#EFF6FF] text-[#2563FF] shadow-[0_1px_3px_rgba(37,99,255,0.12)]'
                    : 'text-[#64748B] hover:text-[#0F172A] hover:bg-[#F8FAFC]'
                }`}
              >
                <item.icon className="w-[18px] h-[18px]" strokeWidth={active ? 2.4 : 2} />
                {item.label}
              </Link>
            );
            // Inject the collapsible Crates group right after Library
            if (item.label === 'Library') {
              return (
                <React.Fragment key="library-and-crates">
                  {node}
                  {/* Web Uploads — all tracks uploaded via the web UI, for quick organising */}
                  <WebUploadsNavGroup
                    location={location}
                    tracks={recentUploads}
                    open={recentUploadsOpen}
                    setOpen={setRecentUploadsOpen}
                  />
                  <CratesNavGroup location={location} />
                </React.Fragment>
              );
            }
            // Inject the collapsible Playlists group right after Sets
            if (item.label === 'Sets') {
              return (
                <React.Fragment key="sets-and-playlists">
                  {node}
                  <PlaylistsNavGroup location={location} />
                </React.Fragment>
              );
            }
            return node;
          })}
        </nav>

        <div className="p-4 border-t border-[#E2E8F0]">
          <CreditBalance />
          <button
            onClick={() => navigate('/profile')}
            className="w-full glass-pane-soft p-3 flex items-center gap-3 hover:shadow-[0_8px_24px_rgba(15,23,42,0.08)] transition-all group mt-2"
            data-testid="sidebar-profile"
          >
            <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center text-white font-bold text-sm shrink-0">
              {firstName.slice(0,1).toUpperCase()}
            </div>
            <div className="flex-1 text-left min-w-0">
              <p className="text-sm font-semibold text-[#0F172A] truncate">{user?.name || firstName}</p>
              <p className="text-[10px] font-mono uppercase tracking-wider text-[#94A3B8]">Pro Plan</p>
            </div>
            <ChevronDown className="w-4 h-4 text-[#94A3B8] group-hover:text-[#2563FF]" />
          </button>
        </div>
      </aside>

      {/* ── Center: Page content ──────────────────────────────────────── */}
      <main className="flex-1 min-w-0 overflow-y-auto" data-testid="shell-main">
        {embeddedHeader ? null : <ShellHeader
          navigate={navigate}
          firstName={firstName}
          libCtx={libCtx}
          location={location}
          notifications={notifications}
          unreadCount={unreadCount}
          notifOpen={notifOpen}
          setNotifOpen={setNotifOpen}
          onOpenSidebar={() => setMobileSidebarOpen(true)}
          onMarkAllRead={() => {
            setNotifications((ns) => {
              const next = ns.map((n) => ({ ...n, read: true }));
              try { localStorage.setItem('cratelab_notifs', JSON.stringify(next)); } catch {}
              return next;
            });
          }}
          onClearAll={() => {
            try { localStorage.removeItem('cratelab_notifs'); } catch {}
            setNotifications([]);
          }}
          onImportClick={() => navigate("/upload")}
        />}
        {children ?? <Outlet />}
      </main>

      {/* Collapsed rail handle — desktop floating tab + mobile FAB */}
      {!railOpen && (
        <>
          <button
            onClick={() => setRailOpen(true)}
            className="hidden xl:flex fixed top-1/2 right-2 -translate-y-1/2 w-9 h-9 z-30 rounded-full gradient-primary text-white items-center justify-center shadow-[0_8px_24px_rgba(37,99,255,0.35)] hover:scale-105 transition-transform"
            data-testid="ai-rail-open"
            title="Open AI Assistant"
          >
            <PanelRightOpen className="w-4 h-4" />
          </button>
          <button
            onClick={() => setRailOpen(true)}
            className="xl:hidden fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full gradient-primary text-white flex items-center justify-center shadow-[0_12px_32px_rgba(37,99,255,0.45)] hover:scale-105 active:scale-95 transition-transform"
            data-testid="ai-rail-open-mobile"
            aria-label="Open AI Assistant"
            title="Open AI Assistant"
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            <Sparkles className="w-5 h-5" />
          </button>
        </>
      )}

      {/* Backdrop for mobile rail drawer */}
      {railOpen && (
        <div
          className="xl:hidden fixed inset-0 bg-black/40 z-40"
          onClick={() => setRailOpen(false)}
          data-testid="ai-rail-backdrop"
        />
      )}

      {/* ── Right Rail: AI Assistant ───────────────────────────────── */}
      <aside
        className={`flex flex-col shrink-0 bg-white border-l border-[#E2E8F0] transition-[width,transform] duration-200 ease-out
          xl:relative xl:inset-auto xl:translate-x-0 xl:z-auto xl:shadow-none xl:max-w-none
          fixed inset-y-0 right-0 z-50 w-[88vw] max-w-[400px] shadow-[0_-12px_40px_rgba(15,23,42,0.18)]
          ${railOpen
            ? 'translate-x-0 xl:w-[360px]'
            : 'translate-x-full xl:translate-x-0 xl:w-0 xl:border-l-0 xl:overflow-hidden'}`}
        data-testid="ai-assistant-rail"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E2E8F0]">
          <div className="flex items-center gap-2 min-w-0">
            <p className="font-mono text-[10px] tracking-[0.18em] text-[#2563FF]">AI ASSISTANT</p>
            {coachPrefs?.playing_style && (
              <span
                data-testid="rail-mode-badge"
                title="Assistant mode — change in Profile → Assistant Preferences"
                className="px-2 py-0.5 rounded-full bg-[#EFF6FF] border border-[#DBEAFE] text-[9px] font-mono tracking-wider uppercase text-[#2563FF]"
              >
                {coachPrefs.playing_style.replace(/_/g, ' ')}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 text-[#94A3B8] relative">
            <div className="relative" ref={pastBtnRef}>
              <button
                onClick={togglePast}
                data-testid="ai-rail-past-chats"
                title="Past chats"
                className="px-2 h-7 rounded-md hover:bg-[#F1F5F9] hover:text-[#2563FF] flex items-center gap-1 text-[10px] font-mono tracking-wider"
              >
                PAST
                <ChevronDown className={`w-3 h-3 transition-transform ${pastOpen ? 'rotate-180' : ''}`} />
              </button>
              {pastOpen && (
                <div
                  data-testid="ai-rail-past-list"
                  className="absolute right-0 top-full mt-1 w-[300px] max-h-[420px] overflow-y-auto bg-white border border-[#E2E8F0] rounded-xl shadow-[0_12px_40px_rgba(15,23,42,0.12)] z-50"
                >
                  <div className="px-3 py-2 border-b border-[#E2E8F0] flex items-center justify-between">
                    <span className="font-mono text-[10px] tracking-[0.18em] text-[#2563FF]">PAST CHATS</span>
                    <span className="text-[10px] text-[#94A3B8] font-mono">
                      {pastSessions ? `${pastSessions.length}` : ''}
                    </span>
                  </div>
                  {pastLoading && (
                    <div className="px-4 py-6 text-center">
                      <Loader2 className="w-4 h-4 text-[#2563FF] animate-spin inline" />
                    </div>
                  )}
                  {!pastLoading && pastSessions && pastSessions.length === 0 && (
                    <div className="px-4 py-6 text-center text-xs text-[#94A3B8] italic">
                      No prior chats yet
                    </div>
                  )}
                  {!pastLoading && pastSessions && pastSessions.map((s) => {
                    const isCurrent = s.session_id === sessionId;
                    return (
                      <div
                        key={s.session_id}
                        className={`group relative flex items-stretch border-b border-[#F1F5F9] last:border-b-0 transition-colors ${
                          isCurrent ? 'bg-[#EFF6FF]' : 'hover:bg-[#F8FAFC]'
                        }`}
                      >
                        <button
                          onClick={() => switchToSession(s.session_id)}
                          data-testid={`ai-rail-past-${s.session_id}`}
                          className="flex-1 min-w-0 text-left px-3 py-2.5"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${isCurrent ? 'bg-[#2563FF]' : 'bg-[#CBD5E1]'}`} />
                            <span className={`text-[12px] font-semibold truncate ${isCurrent ? 'text-[#2563FF]' : 'text-[#0F172A]'}`}>
                              {s.title || 'Untitled chat'}
                            </span>
                            {s.ai_titled && (
                              <Sparkles className="w-3 h-3 text-[#22D3EE] shrink-0" title="AI-generated title" />
                            )}
                          </div>
                          <p className="text-[10px] font-mono text-[#94A3B8] pl-3.5">
                            {formatRelativeTime(s.updated_at)} · {s.message_count} msgs
                          </p>
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deletePastSession(s.session_id); }}
                          data-testid={`ai-rail-past-delete-${s.session_id}`}
                          title="Delete chat"
                          className="opacity-0 group-hover:opacity-100 px-2 my-2 mr-1 rounded-md text-[#94A3B8] hover:bg-red-50 hover:text-red-500 transition-all flex items-center"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <button
              onClick={startNewChat}
              data-testid="ai-rail-new-chat"
              title="Start a new chat (current thread stays saved)"
              className="px-2 h-7 rounded-md hover:bg-[#F1F5F9] hover:text-[#2563FF] flex items-center text-[10px] font-mono tracking-wider"
            >
              NEW
            </button>
            <button onClick={() => setRailOpen(false)} className="w-7 h-7 rounded-md hover:bg-[#F1F5F9] flex items-center justify-center" data-testid="ai-rail-collapse" title="Collapse">
              <PanelRightClose className="w-3.5 h-3.5" />
            </button>
            <button className="w-7 h-7 rounded-md hover:bg-[#F1F5F9] flex items-center justify-center"><MoreVertical className="w-3.5 h-3.5" /></button>
          </div>
        </div>

        {/* Droid avatar + greeting */}
        <div className="px-5 pt-6 pb-4 flex flex-col items-center text-center">
          <div className="relative w-28 h-28 rounded-full overflow-hidden mb-3 shadow-[0_10px_30px_rgba(37,99,255,0.25)] ring-1 ring-[#DBEAFE] bg-white">
            <img
              src="/ai-assistant-avatar.png"
              alt="CRATELAB AI"
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ objectPosition: '60% 30%' }}
            />
            <div className="absolute -inset-1 rounded-full ring-2 ring-[#22D3EE]/40 animate-pulse-glow pointer-events-none" />
          </div>
          <p className="font-heading text-base font-semibold text-[#0F172A]">
            Hi {firstName} <span>👋</span>
          </p>
          <p className="font-body text-xs text-[#64748B] mt-1 max-w-[260px]">
            How can I help you build your perfect set?
          </p>

          {/* Coach Memory — visible proof the bot is learning from past sets */}
          {behaviorProfile && behaviorProfile.sets_built_count > 0 && (
            <div
              className="mt-4 px-3 py-2 rounded-xl border border-[#DBEAFE] bg-[#EFF6FF]/60 backdrop-blur flex items-start gap-2 max-w-[280px] w-full text-left"
              data-testid="coach-memory-pill"
              title="The assistant uses these learned patterns to make smart presumptions instead of asking the same questions every time."
            >
              <Sparkles className="w-3.5 h-3.5 text-[#2563FF] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-mono text-[9px] tracking-[0.18em] text-[#2563FF] uppercase">
                  Assistant Memory · {behaviorProfile.sets_built_count} set{behaviorProfile.sets_built_count === 1 ? '' : 's'}
                </p>
                <p className="font-body text-[11px] text-[#0F172A] leading-snug mt-0.5">
                  {behaviorProfile.top_bpm_range && (
                    <>Knows your <span className="font-mono font-semibold">{behaviorProfile.top_bpm_range[0]}–{behaviorProfile.top_bpm_range[1]} BPM</span></>
                  )}
                  {behaviorProfile.top_genres && behaviorProfile.top_genres.length > 0 && (
                    <> · <span className="text-[#475569]">{behaviorProfile.top_genres[0]}</span></>
                  )}
                  {behaviorProfile.top_curves && behaviorProfile.top_curves.length > 0 && (
                    <> · <span className="text-[#475569]">{behaviorProfile.top_curves[0]} curve</span></>
                  )}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Chat thread */}
        <div className="flex-1 overflow-y-auto px-4 space-y-3 min-h-0" data-testid="assistant-thread">
          {messages.map((m, i) => (
            <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex flex-col items-start'}>
              <div className={`max-w-[85%] px-3.5 py-2 text-sm rounded-2xl whitespace-pre-wrap break-words [overflow-wrap:anywhere] ${
                m.role === 'user'
                  ? 'gradient-primary text-white rounded-tr-sm shadow-[0_4px_12px_rgba(37,99,255,0.25)] self-end'
                  : 'bg-[#F1F5F9] text-[#0F172A] rounded-tl-sm border border-[#E2E8F0]'
              }`}>{m.content}</div>
              {m.role === 'assistant' && m.brief && (
                <div className="mt-2 space-y-2">
                  {m.dryRun && (
                    <div className="space-y-1.5" data-testid={`assistant-dryrun-${i}`}>
                      <div className="px-3 py-2 rounded-lg border bg-[#F8FAFC] border-[#E2E8F0] text-[11px] font-mono text-[#475569]">
                        <span className="font-semibold text-[#2563FF]">📊 BRIEF PREVIEW · </span>
                        {m.dryRun.candidate_count} matching · target {m.dryRun.target_count} · library {m.dryRun.library_total}
                        {m.brief.bpm_min ? ` · ${Math.round(m.brief.bpm_min)}–${Math.round(m.brief.bpm_max || 0)} BPM` : ''}
                        {(m.brief.target_genres || []).length ? ` · ${(m.brief.target_genres || []).slice(0, 2).join(', ')}` : ''}
                      </div>
                      {(m.dryRun.suggestions || []).map((s, si) => (
                        <div key={si} className="px-2.5 py-1.5 rounded-lg border bg-amber-50 text-amber-700 border-amber-200 text-[11px] font-mono">
                          💡 {s}
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={() => buildSetFromBrief(m.brief, i)}
                    disabled={buildingIdx != null}
                    data-testid={`assistant-build-set-${i}`}
                    className="px-3 py-1.5 rounded-full gradient-primary text-white text-xs font-semibold flex items-center gap-1.5 shadow-[0_4px_12px_rgba(37,99,255,0.25)] disabled:opacity-60"
                  >
                    {buildingIdx === i
                      ? <><Loader2 className="w-3 h-3 animate-spin" /> Building…</>
                      : <><Sparkles className="w-3 h-3" /> Save as Set</>}
                  </button>
                </div>
              )}
              {m.role === 'assistant' && m.builtSetId && (
                <div className="mt-2 space-y-2">
                  {/* Mix quality verdict — first thing the user sees after a build */}
                  <MixQualityCard
                    warnings={m.warnings || []}
                    trackCount={m.trackCount || 0}
                    variant="compact"
                    celebrate
                  />
                  {(m.relaxations || []).length > 0 && (
                    <div className="space-y-1" data-testid={`assistant-relaxations-${i}`}>
                      {(m.relaxations || []).slice(0, 3).map((r, ri) => (
                        <div key={ri} className="px-2.5 py-1.5 rounded-lg border bg-[#EFF6FF] text-[#2563FF] border-[#DBEAFE] text-[11px] font-mono">
                          ⚙ {r}
                        </div>
                      ))}
                    </div>
                  )}
                  {(m.warnings || []).length > 0 && (
                    <div className="space-y-1" data-testid={`assistant-warnings-${i}`}>
                      {(m.warnings || []).slice(0, 4).map((w, wi) => {
                        const sev = w.severity === 'high' ? 'red' : w.severity === 'med' ? 'amber' : 'slate';
                        const cls = sev === 'red'
                          ? 'bg-red-50 text-red-700 border-red-200'
                          : sev === 'amber'
                            ? 'bg-amber-50 text-amber-700 border-amber-200'
                            : 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]';
                        return (
                          <div key={wi} className={`px-2.5 py-1.5 rounded-lg border text-[11px] font-mono ${cls}`}>
                            {w.message}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <button
                    onClick={() => navigate('/sets')}
                    data-testid={`assistant-open-sets-${i}`}
                    className="px-3 py-1.5 rounded-full bg-[#EFF6FF] border border-[#DBEAFE] text-[#2563FF] text-xs font-semibold hover:bg-[#DBEAFE]"
                  >
                    Open Sets →
                  </button>
                </div>
              )}
            </div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="px-3.5 py-2 rounded-2xl bg-[#F1F5F9] border border-[#E2E8F0] text-sm text-[#94A3B8]">…</div>
            </div>
          )}
          <div ref={chatEndRef} />
        </div>

        {messages.length <= 1 && (
          <div className="px-4 pb-3 space-y-2">
            <QuickPrompt onClick={() => send('I want to create a techno peak time set for the club.')}>
              I want to create a techno peak time set for the club
            </QuickPrompt>
            <QuickPrompt onClick={() => send('Build me a 90-min warm-up set under 120 BPM.')}>
              Build me a 90-min warm-up set under 120 BPM
            </QuickPrompt>
            <QuickPrompt onClick={() => send('Suggest a closing set to wind things down.')}>
              Suggest a closing set to wind things down
            </QuickPrompt>
          </div>
        )}

        <div
          className={`p-4 border-t border-[#E2E8F0] bg-white transition-[padding] ${playingTrack ? 'pb-[100px]' : ''}`}
          data-testid="assistant-composer"
        >
          <div className="flex items-end gap-2 px-3 py-2 rounded-2xl bg-white border border-[#E2E8F0] focus-within:border-[#2563FF] focus-within:ring-2 focus-within:ring-[#2563FF]/15 transition">
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Type your message… (Shift+Enter for new line)"
              disabled={sending}
              rows={2}
              className="flex-1 bg-transparent border-0 outline-none focus:ring-0 text-sm placeholder:text-[#94A3B8] disabled:opacity-60 resize-none max-h-40 py-1.5 leading-relaxed"
              data-testid="assistant-input"
            />
            <button
              onClick={() => send()}
              disabled={sending || !input.trim()}
              className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center text-white disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_2px_8px_rgba(37,99,255,0.35)] shrink-0"
              data-testid="assistant-send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <CommandPalette onSwitchChatSession={switchToSession} />
    </div>
  );
}

/* ──────────────────────── helpers ──────────────────────── */

function ShellHeader({
  navigate, firstName, libCtx, location,
  notifications = [], unreadCount = 0,
  notifOpen, setNotifOpen, onMarkAllRead, onClearAll, onImportClick,
  onOpenSidebar,
}) {
  const pageMap = {
    '/dashboard': 'Dashboard',
    '/library': 'Library',
    '/crates': 'Crates',
    '/playlists': 'Playlists',
    '/sets': 'AI Generated Sets',
    '/mixes': 'Mixes',
    '/assistant': 'AI Generated Sets',
    '/profile': 'Profile',
  };
  const currentPage = pageMap[location?.pathname] || (location?.pathname?.startsWith('/track/') ? 'Track Detail'
    : location?.pathname?.startsWith('/crate/') ? 'Crate Detail'
    : location?.pathname?.startsWith('/playlist/') ? 'Playlist Detail'
    : 'CRATELAB');
  return (
    <header className="px-4 sm:px-6 pt-4 sm:pt-6 pb-2 flex items-center justify-between gap-3">
      {/* Hamburger — mobile only */}
      {onOpenSidebar && (
        <button
          type="button"
          onClick={onOpenSidebar}
          className="lg:hidden w-10 h-10 rounded-full bg-white border border-[#E2E8F0] flex items-center justify-center text-[#0F172A] hover:border-[#2563FF]/30 transition-colors flex-shrink-0"
          data-testid="shell-hamburger"
          aria-label="Open menu"
        >
          <Menu className="w-5 h-5" />
        </button>
      )}
      <div className="min-w-0 flex-1">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#EFF6FF] border border-[#DBEAFE] mb-2">
          <span className="w-1.5 h-1.5 rounded-full bg-[#14E6C9] animate-pulse" />
          <span className="font-mono text-[10px] tracking-[0.18em] text-[#2563FF]">{currentPage.toUpperCase()}</span>
          {libCtx?.tracks_count != null && (
            <span className="font-mono text-[10px] text-[#94A3B8]">· {libCtx.tracks_count} tracks</span>
          )}
        </div>
        <h1 className="font-heading text-lg sm:text-xl font-semibold text-[#0F172A] tracking-tight truncate">
          {currentPage === 'Library' ? `Browse your ${libCtx?.tracks_count || ''} tracks` :
           currentPage === 'Crates' ? 'Organize your sound' :
           currentPage === 'Playlists' ? 'Curate your playlists' :
           currentPage === 'AI Generated Sets' ? 'Your AI-built sets' :
           currentPage === 'History' ? 'Your mix history' :
           currentPage === 'Profile' ? 'Profile & settings' :
           currentPage === 'Track Detail' ? 'Track analysis' :
           currentPage === 'Crate Detail' ? 'Crate contents' :
           currentPage === 'Playlist Detail' ? 'Playlist' :
           `Hey ${firstName} 👋`}
        </h1>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <button
          onClick={() => window.dispatchEvent(new CustomEvent('cratelab:open-palette'))}
          data-testid="shell-search-btn"
          className="hidden md:flex items-center gap-2 px-4 py-2.5 rounded-full bg-white border border-[#E2E8F0] w-[320px] shadow-[0_1px_3px_rgba(15,23,42,0.04)] hover:border-[#2563FF]/30 transition-colors text-left"
        >
          <Search className="w-4 h-4 text-[#94A3B8]" />
          <span className="flex-1 text-sm text-[#94A3B8]">Search tracks, crates, chats…</span>
          <kbd className="px-1.5 py-0.5 rounded border border-[#E2E8F0] text-[10px] font-mono text-[#94A3B8]">
            ⌘K
          </kbd>
        </button>

        {/* Notifications — fed by AI Assistant proactive messages */}
        <div className="relative">
          <button
            onClick={() => {
              setNotifOpen((v) => !v);
              if (!notifOpen && unreadCount > 0) onMarkAllRead?.();
            }}
            className="relative w-10 h-10 rounded-full bg-white border border-[#E2E8F0] flex items-center justify-center text-[#64748B] hover:text-[#2563FF] hover:border-[#2563FF]/30 transition-colors"
            data-testid="shell-notifications"
            title="Assistant activity"
            aria-label={`${unreadCount} unread notifications`}
          >
            <Bell className="w-4 h-4" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full gradient-primary text-white text-[10px] font-bold flex items-center justify-center shadow-[0_3px_8px_rgba(37,99,255,0.45)]">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setNotifOpen(false)} />
              <div
                className="absolute right-0 top-[calc(100%+8px)] w-[360px] max-h-[480px] glass-pane-strong z-50 overflow-hidden flex flex-col"
                data-testid="shell-notifications-popover"
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-[#E2E8F0]">
                  <div>
                    <p className="font-heading text-sm font-semibold text-[#0F172A]">Assistant activity</p>
                    <p className="font-mono text-[10px] tracking-wider text-[#94A3B8]">
                      {notifications.length === 0 ? 'No activity yet' : `${notifications.length} item${notifications.length === 1 ? '' : 's'}`}
                    </p>
                  </div>
                  {notifications.length > 0 && (
                    <button
                      onClick={() => {
                        onClearAll?.();
                        setNotifOpen(false);
                      }}
                      className="text-[10px] font-mono text-[#94A3B8] hover:text-[#2563FF] transition-colors"
                    >
                      CLEAR ALL
                    </button>
                  )}
                </div>
                <div className="overflow-y-auto flex-1">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-10 text-center">
                      <div className="w-12 h-12 mx-auto rounded-2xl bg-gradient-to-br from-[#EFF6FF] to-[#ECFEFF] border border-[#DBEAFE] flex items-center justify-center mb-3">
                        <Bell className="w-5 h-5 text-[#2563FF]" />
                      </div>
                      <p className="font-body text-sm text-[#475569]">You're all caught up</p>
                      <p className="font-body text-xs text-[#94A3B8] mt-1 leading-snug">
                        The assistant will log proactive messages, set rebuilds, and library updates here.
                      </p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-[#E2E8F0]">
                      {notifications.map((n) => (
                        <li key={n.id} className="px-4 py-3 hover:bg-[#F8FAFC] transition-colors">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#EFF6FF] to-[#ECFEFF] border border-[#DBEAFE] flex items-center justify-center shrink-0">
                              <Bell className="w-3.5 h-3.5 text-[#2563FF]" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center justify-between gap-2">
                                <p className="font-medium text-sm text-[#0F172A] truncate">{n.title}</p>
                                <p className="font-mono text-[10px] text-[#94A3B8] shrink-0">{formatRelativeTime(new Date(n.ts).toISOString())}</p>
                              </div>
                              <p className="font-body text-xs text-[#475569] mt-1 line-clamp-3 whitespace-pre-line">{n.body}</p>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <button onClick={onImportClick} className="btn-primary flex items-center gap-2 text-sm" data-testid="shell-import">
          <Upload className="w-4 h-4" /> <span className="hidden sm:inline">Import Tracks</span>
        </button>
      </div>
    </header>
  );
}

function QuickPrompt({ children, onClick }) {
  return (
    <button onClick={onClick} className="w-full text-left px-3 py-2 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] hover:border-[#2563FF]/40 hover:bg-[#EFF6FF] text-xs text-[#475569] transition-colors">
      {children}
    </button>
  );
}

function WebUploadsNavGroup({ location, tracks, open, setOpen }) {
  const sectionActive = location.pathname === '/library' && location.search.includes('source=web');
  return (
    <div className="select-none" data-testid="sidebar-recent-uploads">
      <div className={`group flex items-center gap-1 rounded-xl text-[15px] font-medium transition-all ${
        sectionActive
          ? 'bg-[#EFF6FF] text-[#2563FF] shadow-[0_1px_3px_rgba(37,99,255,0.12)]'
          : 'text-[#64748B] hover:text-[#0F172A] hover:bg-[#F8FAFC]'
      }`}>
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="sidebar-recent-uploads-toggle"
          className="w-6 h-9 flex items-center justify-center text-current ml-1.5 rounded hover:bg-black/[0.04]"
          aria-label={open ? 'Collapse Web Uploads' : 'Expand Web Uploads'}
        >
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
        <Link
          to="/library?source=web"
          data-testid="sidebar-nav-upload-section"
          className="flex items-center gap-2.5 flex-1 py-2 -ml-1"
        >
          <Upload className="w-[18px] h-[18px]" strokeWidth={sectionActive ? 2.4 : 2} />
          Web Uploads
          {tracks.length > 0 && (
            <span className="ml-1 text-[10px] font-mono text-[#94A3B8]">{tracks.length}</span>
          )}
        </Link>
      </div>
      {open && (
        <ul className="mt-1 ml-3 pl-3 border-l border-[#E2E8F0] space-y-0.5 max-h-48 overflow-y-auto" data-testid="sidebar-recent-uploads-list">
          {tracks.length === 0 ? (
            <li className="px-3 py-2 text-[11px] text-[#94A3B8] italic">No web uploads yet</li>
          ) : (
            tracks.map((track) => (
              <li key={track.id}>
                <Link
                  to={`/track/${track.id}`}
                  data-testid={`sidebar-recent-upload-${track.id}`}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] transition-all ${
                    location.pathname === `/track/${track.id}`
                      ? 'bg-[#EFF6FF] text-[#2563FF] font-semibold'
                      : 'text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC]'
                  }`}
                >
                  <div className="w-6 h-6 rounded-md overflow-hidden border border-[#E2E8F0] bg-[#EFF6FF] flex items-center justify-center shrink-0">
                    <SafeImg
                      src={track.cover_url}
                      alt=""
                      className="w-full h-full object-cover"
                      fallbackClassName="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#EFF6FF] to-[#ECFEFF]"
                      iconClassName="w-3 h-3 text-[#2563FF]"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{track.title || 'Untitled'}</p>
                    <p className="text-[10px] text-[#94A3B8] truncate">{track.artist || 'Unknown'}</p>
                  </div>
                </Link>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

function CratesNavGroup({ location }) {
  return (
    <SidebarLibraryGroup
      kind="crate"
      label="Crates"
      icon={FolderOpen}
      apiList="/crates"
      apiCreate="/crates"
      listKey="crates"
      detailPath="/crate"
      indexPath="/crates"
      lsKey="cratelab_crates_open"
      testidPrefix="sidebar-crates"
      itemTestid="sidebar-crate"
      navTestid="sidebar-nav-crates"
      location={location}
      makeColor
    />
  );
}

function PlaylistsNavGroup({ location }) {
  return (
    <SidebarLibraryGroup
      kind="playlist"
      label="Playlists"
      icon={Disc3}
      apiList="/playlists?include_suggested=false"
      apiCreate="/playlists"
      listKey="playlists"
      detailPath="/playlist"
      indexPath="/playlists"
      lsKey="cratelab_playlists_open"
      testidPrefix="sidebar-playlists"
      itemTestid="sidebar-playlist"
      navTestid="sidebar-nav-playlists"
      location={location}
    />
  );
}

const CRATE_PALETTE = ['#2563FF', '#22D3EE', '#14E6C9', '#8b5cf6', '#ec4899', '#f97316', '#eab308', '#22c55e'];

function SidebarLibraryGroup({
  kind, label, icon: Icon, apiList, apiCreate, listKey, detailPath, indexPath,
  lsKey, testidPrefix, itemTestid, navTestid, location, makeColor,
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(lsKey) !== '0'; } catch { return true; }
  });
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [dragOverId, setDragOverId] = useState(null);
  const newInputRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem(lsKey, open ? '1' : '0'); } catch {}
  }, [open, lsKey]);

  const fetchItems = async () => {
    try {
      const { data } = await axios.get(`${API}${apiList}`);
      setItems(data?.[listKey] || []);
    } catch {
      /* silent — UI will show empty state */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  // Auto-open the sidebar section when the user navigates into it. We used to
  // ALSO re-fetch here (`fetchItems()`), which meant every /crates ↔ /crate/:id
  // nav re-hit the endpoint (~600ms of spinner each time). Removed — the
  // initial-mount fetch is enough; new crates/playlists are added optimistically
  // via submitNew, and the next full page refresh picks up any external changes.
  useEffect(() => {
    const isInSection = location.pathname === indexPath || location.pathname.startsWith(`${detailPath}/`);
    if (isInSection) setOpen(true);
  }, [location.pathname]);

  useEffect(() => {
    if (adding) {
      const t = setTimeout(() => newInputRef.current?.focus(), 40);
      return () => clearTimeout(t);
    }
  }, [adding]);

  const sectionActive = location.pathname === indexPath || location.pathname.startsWith(`${detailPath}/`);

  const submitNew = async () => {
    const name = newName.trim();
    if (!name || creating) return;
    setCreating(true);
    try {
      const body = makeColor
        ? { name, description: '', color: CRATE_PALETTE[items.length % CRATE_PALETTE.length] }
        : { name, description: '' };
      const { data } = await axios.post(`${API}${apiCreate}`, body);
      const created = data?.[kind] || data?.playlist || data?.crate || { id: data?.id, name };
      setItems((c) => [...c, created]);
      setNewName('');
      setAdding(false);
      toast.success(`${label.slice(0, -1)} "${name}" created`);
      setOpen(true);
      if (created?.id) navigate(`${detailPath}/${created.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not create ${label.toLowerCase().slice(0, -1)}`);
    } finally {
      setCreating(false);
    }
  };

  const handleDropOnItem = async (e, item) => {
    e.preventDefault();
    setDragOverId(null);
    let trackIds = [];
    try {
      const raw = e.dataTransfer.getData('trackIds');
      trackIds = raw ? JSON.parse(raw) : [];
    } catch {
      trackIds = [];
    }
    if (!trackIds.length || !item?.id) return;
    try {
      if (kind === 'crate') {
        await axios.post(`${API}/crates/${item.id}/tracks/batch`, trackIds);
      } else {
        // No batch endpoint for playlists — fire requests in parallel
        await Promise.all(trackIds.map((tid) =>
          axios.post(`${API}/playlists/${item.id}/tracks/${tid}`).catch(() => null),
        ));
      }
      toast.success(
        trackIds.length > 1
          ? `Added ${trackIds.length} tracks to "${item.name}"`
          : `Added to "${item.name}"`,
        { action: { label: 'Open', onClick: () => navigate(`${detailPath}/${item.id}`) } },
      );
      // Optimistically bump the count for the dropped item
      setItems((arr) => arr.map((x) => x.id === item.id
        ? { ...x, track_ids: Array.from(new Set([...(x.track_ids || []), ...trackIds])) }
        : x,
      ));
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not add to ${label.toLowerCase().slice(0, -1)}`);
    }
  };

  return (
    <div className="select-none" data-testid={`${testidPrefix}-group`}>
      <div className={`group flex items-center gap-1 rounded-xl text-[15px] font-medium transition-all ${
        sectionActive
          ? 'bg-[#EFF6FF] text-[#2563FF] shadow-[0_1px_3px_rgba(37,99,255,0.12)]'
          : 'text-[#64748B] hover:text-[#0F172A] hover:bg-[#F8FAFC]'
      }`}>
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid={`${testidPrefix}-toggle`}
          className="w-6 h-9 flex items-center justify-center text-current ml-1.5 rounded hover:bg-black/[0.04]"
          aria-label={open ? `Collapse ${label}` : `Expand ${label}`}
        >
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
        <Link
          to={indexPath}
          data-testid={navTestid}
          className="flex items-center gap-2.5 flex-1 py-2 -ml-1"
        >
          <Icon className="w-[18px] h-[18px]" strokeWidth={sectionActive ? 2.4 : 2} />
          {label}
          {!loading && items.length > 0 && (
            <span className="ml-1 text-[10px] font-mono text-[#94A3B8]">{items.length}</span>
          )}
        </Link>
        <button
          onClick={(e) => { e.stopPropagation(); setOpen(true); setAdding(true); }}
          data-testid={`${testidPrefix}-add`}
          title={`New ${label.toLowerCase().slice(0, -1)}`}
          className="w-7 h-7 mr-1.5 rounded-md flex items-center justify-center text-current hover:bg-[#2563FF] hover:text-white opacity-70 hover:opacity-100 transition-all"
        >
          <Plus className="w-3.5 h-3.5" />
        </button>
      </div>

      {open && (
        <ul className="mt-1 ml-3 pl-3 border-l border-[#E2E8F0] space-y-0.5" data-testid={`${testidPrefix}-list`}>
          {loading && (
            <li className="px-3 py-1.5 text-[10px] font-mono text-[#94A3B8] flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading…
            </li>
          )}
          {!loading && items.length === 0 && !adding && (
            <li className="px-3 py-1.5 text-[11px] text-[#94A3B8] italic">No {label.toLowerCase()} yet</li>
          )}
          {items.map((c) => {
            const active = location.pathname === `${detailPath}/${c.id}`;
            const trackCount = (c.active_track_count ?? c.track_ids?.length ?? 0);
            return (
              <li key={c.id}>
                <Link
                  to={`${detailPath}/${c.id}`}
                  data-testid={`${itemTestid}-${c.id}`}
                  onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setDragOverId(c.id); }}
                  onDragEnter={(e) => { e.preventDefault(); setDragOverId(c.id); }}
                  onDragLeave={() => setDragOverId((prev) => prev === c.id ? null : prev)}
                  onDrop={(e) => handleDropOnItem(e, c)}
                  className={`group flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] transition-all ${
                    dragOverId === c.id
                      ? 'bg-[#DBEAFE] text-[#2563FF] ring-2 ring-[#22D3EE]/60 shadow-[0_4px_14px_rgba(37,99,255,0.25)] scale-[1.02]'
                      : active
                        ? 'bg-[#EFF6FF] text-[#2563FF] font-semibold'
                        : 'text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC]'
                  }`}
                >
                  {makeColor ? (
                    <span
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ background: c.color || '#2563FF' }}
                    />
                  ) : (
                    <span className={`w-2 h-2 rounded-full shrink-0 ${c.is_suggested ? 'bg-[#14E6C9]' : 'bg-[#2563FF]'}`} />
                  )}
                  <span className="flex-1 truncate">{c.name}</span>
                  {trackCount > 0 && (
                    <span className="text-[10px] font-mono text-[#94A3B8] opacity-60 group-hover:opacity-100">{trackCount}</span>
                  )}
                </Link>
              </li>
            );
          })}
          {adding && (
            <li className="pr-2 pl-1 py-1">
              <div className="flex items-center gap-1.5">
                <input
                  ref={newInputRef}
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); submitNew(); }
                    if (e.key === 'Escape') { setAdding(false); setNewName(''); }
                  }}
                  placeholder={`${label.slice(0, -1)} name…`}
                  data-testid={`${testidPrefix}-new-input`}
                  disabled={creating}
                  maxLength={60}
                  className="flex-1 min-w-0 px-2 py-1.5 rounded-lg border border-[#2563FF]/40 bg-white text-[12px] outline-none focus:ring-2 focus:ring-[#2563FF]/20 disabled:opacity-60"
                />
                <button
                  onClick={submitNew}
                  disabled={!newName.trim() || creating}
                  data-testid={`${testidPrefix}-new-submit`}
                  className="w-7 h-7 shrink-0 rounded-lg gradient-primary text-white flex items-center justify-center disabled:opacity-40"
                  title="Create"
                >
                  {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                </button>
              </div>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
